import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useInView } from 'framer-motion';

const EASE_REFINED = [0.22, 1, 0.36, 1];

const faqs = [
  {
    question: 'How does Stavyn predict who\'s about to leave?',
    answer: 'Stavyn\'s AI analyzes 47 distinct risk factors for every employee, including compensation vs. market benchmarks, tenure trajectory, recent performance signals, competitor hiring activity in their skill set, and behavioral patterns. It also references past turnover cases to identify patterns that typically show up before resignations.',
  },
  {
    question: 'What makes this different from employee surveys?',
    answer: 'Surveys tell you what employees SAY. We show you what the MARKET says. By the time someone admits they\'re unhappy in a survey, they\'ve already updated their LinkedIn. Stavyn catches risk signals months earlier by tracking external market conditions like salary trends, competitor hiring, and job demand.',
  },
  {
    question: 'How quickly can I see who\'s at risk?',
    answer: 'Import your team data and get your first flight risk report in under 5 minutes. Our AI immediately compares every employee against current market conditions, salary benchmarks, and competitor hiring activity. No complex integrations required.',
  },
  {
    question: 'What data do you need from us?',
    answer: 'The minimum required fields are: employee name, role, current salary, and start date. You can import via HRIS integration (BambooHR, Workday, ADP, Gusto), ATS integration, or a simple CSV. The more context you provide (team, manager, performance tier), the more precise the AI recommendations become.',
  },
  {
    question: 'Is my employee data secure?',
    answer: 'We take security seriously. All data is encrypted in transit and at rest. Employee data is not sold to third parties and can be deleted on request.',
  },
];

function FAQItem({ faq, index, inView, embedded = false }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.45, ease: EASE_REFINED, delay: index * 0.06 }}
      className={`faq-item${embedded ? ' stories-faq-bridge__faq-item' : ''}`}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '2rem',
          padding: '1.5rem 0',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
        aria-expanded={isOpen}
      >
        <span style={{
          fontFamily: 'DM Sans, Inter, sans-serif',
          fontWeight: 500,
          fontSize: '0.9375rem',
          lineHeight: 1.45,
          color: 'var(--text-primary)',
          transition: 'color 0.2s ease',
          letterSpacing: '0.005em',
          flex: 1,
        }}>
          {faq.question}
        </span>

        {/* Icon: + rotates to × */}
        <motion.div
          animate={{ rotate: isOpen ? 45 : 0 }}
          transition={{ duration: 0.25, ease: EASE_REFINED }}
          style={{
            width: 20,
            height: 20,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isOpen ? (embedded ? 'var(--sfb-accent)' : 'var(--accent-blue)') : (embedded ? 'var(--sfb-accent)' : 'var(--text-muted)'),
            transition: 'color 0.2s ease',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 1V13M1 7H13" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round"/>
          </svg>
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE_REFINED }}
            style={{ overflow: 'hidden' }}
          >
            <p style={{
              fontFamily: 'DM Sans, Inter, sans-serif',
              fontWeight: 400,
              fontSize: '0.9375rem',
              lineHeight: 1.75,
              color: 'var(--text-secondary)',
              paddingBottom: '1.5rem',
              maxWidth: 680,
            }}>
              {faq.answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function FAQSection({ embedded = false }) {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, margin: '-80px' });

  return (
    <motion.section
      ref={sectionRef}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: EASE_REFINED }}
      style={{
        background: embedded ? 'transparent' : 'var(--bg-base)',
        padding: embedded ? '80px 0 120px' : '120px 0',
        borderTop: embedded ? 'none' : '1px solid var(--border-divider)',
      }}
    >
      <div className="max-w-7xl mx-auto px-8 lg:px-12">
        <div className="grid lg:grid-cols-3 gap-16 lg:gap-24">

          {/* Left — header */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, ease: EASE_REFINED }}
            >
              <div className={`section-tag mb-8${embedded ? ' stories-faq-bridge__tag' : ''}`}>Common Questions</div>
              <h2
                className="landing-heading"
                style={{
                fontSize: 'clamp(1.625rem, 3vw, 2.25rem)',
                lineHeight: 1.1,
                color: 'var(--text-primary)',
                marginBottom: '1.25rem',
              }}
              >
                Everything you need{' '}
                <span className="headline-accent">to know.</span>
              </h2>
              <p style={{
                fontFamily: 'DM Sans, Inter, sans-serif',
                fontWeight: 400,
                fontSize: '0.875rem',
                lineHeight: 1.72,
                color: 'var(--text-muted)',
              }}>
                Have more questions? We'd be glad to walk you through exactly how Stavyn works for your organization.
              </p>
              <motion.div
                initial={{ opacity: 0 }}
                animate={inView ? { opacity: 1 } : {}}
                transition={{ duration: 0.5, ease: EASE_REFINED, delay: 0.3 }}
                style={{ marginTop: '1.75rem' }}
              >
                <Link
                  to="/contact"
                  style={{
                    fontFamily: 'Inter, DM Sans, sans-serif',
                    fontWeight: 500,
                    fontSize: '0.8125rem',
                    letterSpacing: '0.02em',
                    color: embedded ? 'var(--sfb-accent)' : 'var(--accent-blue)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    cursor: 'pointer',
                    textDecoration: 'none',
                    transition: 'opacity 0.2s ease',
                  }}
                  onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
                  onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                >
                  Contact us
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M1 5h8M6 2l3 3-3 3" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </Link>
              </motion.div>
            </motion.div>
          </div>

          {/* Right — accordion */}
          <div className={`lg:col-span-2${embedded ? ' stories-faq-bridge__faq-panel' : ''}`}>
            {!embedded && (
            <motion.div
              initial={{ scaleX: 0 }}
              animate={inView ? { scaleX: 1 } : {}}
              transition={{ duration: 0.7, ease: EASE_REFINED }}
              style={{
                height: 1,
                background: 'var(--border-main)',
                transformOrigin: 'left',
                marginBottom: 0,
              }}
            />
            )}
            {faqs.map((faq, i) => (
              <FAQItem key={i} faq={faq} index={i} inView={inView} embedded={embedded} />
            ))}
          </div>
        </div>
      </div>
    </motion.section>
  );
}
